import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";

const PAGE_SIZE = 10;

const getToken = () =>
  localStorage.getItem("adminToken") ||
  localStorage.getItem("token") ||
  (() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "{}")?.token || "";
    } catch {
      return "";
    }
  })();

const safeText = (v) => (v === null || v === undefined ? "" : String(v));
const safeTextDash = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
const lower = (v) => safeText(v).toLowerCase();

const dtIN = (d) => {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleString("en-IN");
};

const roleText = (u) => u?.role || u?.userType || u?.type || "N/A";

const AllUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();
        if (!token) {
          setUsers([]);
          setError("Admin token not found. Please login again.");
          return;
        }

        // ✅ ADMIN endpoint (because /api/user/all-users uses authUserMiddleware)
        const res = await axios.get(`${baseurl}/api/admin/user/all-users`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const data = Array.isArray(res.data) ? res.data : res.data?.users || [];
        setUsers(data);
      } catch (err) {
        console.error("Fetch users error:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Failed to fetch users.");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = lower(search.trim());
    if (!q) return users;

    return users.filter((u) => {
      return (
        lower(u?._id).includes(q) ||
        lower(u?.name).includes(q) ||
        lower(u?.fullName).includes(q) ||
        lower(u?.email).includes(q) ||
        lower(u?.mobile).includes(q) ||
        lower(roleText(u)).includes(q)
      );
    });
  }, [users, search]);

  useEffect(() => setPage(1), [search]);

  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filteredUsers.slice(start, start + PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading users…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">All Users</h1>
            <p className="text-sm text-gray-600 mt-1">
              Search by ID / Name / Email / Mobile / Role
            </p>
          </div>

          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Total: <b>{users.length}</b> • Showing: <b>{filteredUsers.length}</b>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
          <label className="text-xs text-gray-500">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ID / Name / Email / Mobile / Role..."
            className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">User</th>
                <th className="px-4 py-3 text-left font-semibold">Email / Mobile</th>
                <th className="px-4 py-3 text-left font-semibold">Role</th>
                <th className="px-4 py-3 text-left font-semibold">Verified</th>
                <th className="px-4 py-3 text-left font-semibold">Blocked</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
                <th className="px-4 py-3 text-left font-semibold">Updated</th>
              </tr>
            </thead>

            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                pageItems.map((u) => (
                  <tr key={u?._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">
                        {safeTextDash(u?.name || u?.fullName)}
                      </div>
                      <div className="text-xs text-gray-500 font-mono break-all">
                        {safeTextDash(u?._id)}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-gray-800">{safeTextDash(u?.email)}</div>
                      <div className="text-xs text-gray-500">{safeTextDash(u?.mobile)}</div>
                    </td>

                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                        {safeTextDash(roleText(u))}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          u?.isVerified ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {u?.isVerified ? "Yes" : "No"}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          u?.isBlocked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                        }`}
                      >
                        {u?.isBlocked ? "Yes" : "No"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">{dtIN(u?.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{dtIN(u?.updatedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Page <b>{page}</b> of <b>{totalPages}</b>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Prev
                </button>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Uses admin route <b>/api/admin/user/all-users</b> with token from <b>adminToken</b> or <b>token</b>.
        </p>
      </div>
    </div>
  );
};

export default AllUsersPage;





























// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { baseurl } from "../config/url";

// const AllUsersPage = () => {
//   const [users, setUsers] = useState([]);
//   const [filteredUsers, setFilteredUsers] = useState([]);
//   const [search, setSearch] = useState("");
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Fetch users data from the API
//   useEffect(() => {
//     const fetchUsers = async () => {
//       try {
//         setLoading(true);
//         const response = await axios.get(`${baseurl}/api/admin/all-users`);
//         setUsers(response.data);
//         setFilteredUsers(response.data);
//         setLoading(false);
//       } catch (err) {
//         setError(err.message || "An error occurred while fetching users.");
//         setLoading(false);
//       }
//     };

//     fetchUsers();
//   }, []);

//   // Filter users based on the search input
//   useEffect(() => {
//     setFilteredUsers(
//       users.filter(
//         (user) =>
//           user.id.toString().includes(search) ||
//           user.name.toLowerCase().includes(search.toLowerCase())
//       )
//     );
//   }, [search, users]);

//   if (loading) return <div>Loading...</div>;
//   if (error) return <div>Error: {error}</div>;

//   return (
//     <div className="p-6 max-w-6xl mx-auto">
//       <h1 className="text-2xl font-bold mb-6 text-center">All Users</h1>

//       <div className="mb-4">
//         <input
//           type="text"
//           className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
//           placeholder="Search by ID or Name"
//           value={search}
//           onChange={(e) => setSearch(e.target.value)}
//         />
//       </div>

//       <div className="overflow-x-auto shadow-md rounded-lg">
//         <table className="table-auto w-full border border-gray-300">
//           <thead>
//             <tr className="bg-blue-100 text-blue-900">
//               <th className="px-4 py-2 border">ID</th>
//               <th className="px-4 py-2 border">Name</th>
//               <th className="px-4 py-2 border">Email</th>
//               <th className="px-4 py-2 border">Mobile</th>
//               <th className="px-4 py-2 border">Type</th>
//               <th className="px-4 py-2 border">Verified</th>
//               <th className="px-4 py-2 border">Blocked</th>
//               <th className="px-4 py-2 border">Created At</th>
//               <th className="px-4 py-2 border">Updated At</th>
//             </tr>
//           </thead>
//           <tbody>
//             {filteredUsers.length > 0 ? (
//               filteredUsers.map((user) => (
//                 <tr key={user.id} className="text-center hover:bg-gray-100">
//                   <td className="px-4 py-2 border">{user.id}</td>
//                   <td className="px-4 py-2 border">{user.name}</td>
//                   <td className="px-4 py-2 border">{user.email}</td>
//                   <td className="px-4 py-2 border">{user.mobile}</td>
//                   <td className="px-4 py-2 border">{user.type}</td>
//                   <td className="px-4 py-2 border">{user.isVerified ? "Yes" : "No"}</td>
//                   <td className="px-4 py-2 border">{user.isBlocked ? "Yes" : "No"}</td>
//                   <td className="px-4 py-2 border">{new Date(user.createdAt).toLocaleString()}</td>
//                   <td className="px-4 py-2 border">{new Date(user.updatedAt).toLocaleString()}</td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="9" className="text-center p-4">
//                   No users found.
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default AllUsersPage;
